import { api } from "../../services/ApiService.js";
import FormValidator from "./FormValidator.js";

export default class FormHandler {
    /**
     * @param {HTMLFormElement|NodeList|HTMLCollection|HTMLFormElement[]} forms - Form or collection of forms.
     * @param {Object} [options]
     * @param {Function} [options.onSubmit] - Custom submit function.
     * @param {Function} [options.onSuccess] - Callback after successful submission.
     */
    constructor(forms, options = {}) {
        this.options = options;

        this.forms = this.normalizeForms(forms);
        this.form = this.forms[0] ?? null; // Backward compatibility.

        this.validators = new WeakMap();
        this.initializedForms = new WeakSet();
        this.loadingForms = new WeakSet();

        this.submitApi = options.onSubmit || ((formData) => api.submitForm(formData));

        this.init();
    }

    /**
     * Normalize any supported form input into an array of HTML forms.
     *
     * @param {HTMLFormElement|NodeList|HTMLCollection|HTMLFormElement[]|string} forms
     * @returns {HTMLFormElement[]}
     */
    normalizeForms(forms) {
        if (!forms) {
            return [];
        }

        if (typeof forms === "string") {
            return [...document.querySelectorAll(forms)];
        }

        if (forms instanceof HTMLFormElement) {
            return [forms];
        }

        if (
            NodeList.prototype.isPrototypeOf(forms) ||
            HTMLCollection.prototype.isPrototypeOf(forms) ||
            Array.isArray(forms)
        ) {
            return [...forms].filter((form) => form instanceof HTMLFormElement);
        }

        return [];
    }

    /**
     * Initialize all forms.
     *
     * Repeated calls are safe and will not create duplicate validators.
     *
     * @param {HTMLFormElement[]} forms
     */
    init(forms = this.forms) {
        forms.forEach((form) => {
            if (this.initializedForms.has(form)) {
                return;
            }

            const validator = new FormValidator(form, (e) => this.handleSubmit(e, form));

            this.validators.set(form, validator);
            this.initializedForms.add(form);
        });
    }

    /**
     * Handle form submission.
     *
     * @param {Event} e
     * @param {HTMLFormElement} form
     */
    async handleSubmit(e, form) {
        e?.preventDefault();

        if (!form || this.loadingForms.has(form)) {
            return;
        }

        this.setLoadingState(form, true);
        this.loadingForms.add(form);

        try {
            const formData = new FormData(form);
            const result = await this.submitApi(formData, form);

            if (result?.success) {
                form.reset();
                this.validators.get(form)?.refresh();

                if (typeof this.options.onSuccess === "function") {
                    try {
                        this.options.onSuccess(result, form);
                    } catch (error) {
                        console.error("Form success callback error:", error);
                    }
                }

                this.showResponse(form, result?.data?.response_html, "success");
                return;
            }

            console.error("Form submission error:", result?.message);
            this.showResponse(form, result?.data?.response_html, "error", result?.message);
        } catch (error) {
            console.error("Form submission error:", error);
            this.showResponse(
                form,
                error?.response?.data?.response_html,
                "error",
                error?.response?.message || error?.message,
            );
        } finally {
            this.loadingForms.delete(form);
            this.setLoadingState(form, false);
        }
    }

    /**
     * Replace a submitted form with the response HTML rendered by the server.
     *
     * @param {HTMLFormElement} form
     * @param {string} [html]
     * @param {"success"|"error"} status
     * @param {string} [message]
     */
    showResponse(form, html, status, message = "") {
        const container = form.closest(".form-container");
        const response = container?.querySelector("[data-form-response]");

        if (!(response instanceof HTMLElement)) {
            console.error("Form response container is missing.");
            return;
        }

        if (typeof html === "string" && html.trim()) {
            response.innerHTML = html;
        } else {
            console.error(`Form ${status} response HTML is missing.`);
            response.textContent =
                status === "success"
                    ? "Дякуємо! Заявку успішно відправлено."
                    : "Не вдалося відправити заявку. Спробуйте ще раз пізніше.";
        }

        if (status === "error" && message) {
            const details = document.createElement("p");
            details.textContent = message;
            response.append(details);
        }

        response.hidden = false;
        response.setAttribute("role", status === "error" ? "alert" : "status");
        form.hidden = true;

        requestAnimationFrame(() => {
            response.focus({ preventScroll: true });
            response.scrollIntoView({
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                    ? "auto"
                    : "smooth",
                block: "center",
            });
        });
    }

    /**
     * Toggle loading state for a specific form.
     *
     * @param {HTMLFormElement} form
     * @param {boolean} isLoading
     */
    setLoadingState(form, isLoading) {
        form.classList.toggle("is-loading", isLoading);
    }

    /**
     * Refresh form validator.
     *
     * @param {HTMLFormElement} form
     * @returns {void}
     */
    refreshValidator(form) {
        if (!(form instanceof HTMLFormElement)) {
            return;
        }
        this.validators.get(form)?.refresh();
    }
}
